import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

/**
 * SubmissionErrorDialog Component
 *
 * Displays when survey submission fails but data was successfully saved.
 * Provides clear feedback about what succeeded and what failed.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback function when dialog is closed
 */
const SubmissionErrorDialog = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="submission-error-dialog-title"
      aria-describedby="submission-error-dialog-description"
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiBackdrop-root': {
          height: '150vh',
        },
      }}
    >
      <DialogTitle id="submission-error-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarningIcon color="warning" sx={{ fontSize: 32 }} />
          <Typography variant="h6" component="span" style={{ fontFamily: 'Lato' }}>
            Submission Issue
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText id="submission-error-dialog-description">
          <Typography variant="body1" paragraph style={{ fontFamily: 'Lato' }}>
            Your survey responses have been successfully saved, but there was an issue 
            with the final submission process.
          </Typography>
        </DialogContentText>

        {/* What Worked */}
        <Box sx={{ mt: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography 
              variant="subtitle2" 
              style={{ 
                fontFamily: 'Lato',
                fontWeight: 'bold',
                color: '#2e7d32'
              }}
            >
              Successfully Completed:
            </Typography>
          </Box>
          <Typography variant="body2" style={{ fontFamily: 'Lato', marginLeft: 28 }}>
            • All your survey responses have been saved securely<br />
            • Your progress is fully preserved<br />
            • No data has been lost
          </Typography>
        </Box>

        {/* What to Do Next */}
        <Box 
          sx={{ 
            mt: 3, 
            p: 2, 
            backgroundColor: 'warning.50',
            border: 1,
            borderColor: 'warning.main',
            borderRadius: 1
          }}
        >
          <Typography variant="subtitle2" color="warning.main" gutterBottom>
            Next Steps:
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Your responses are safely saved and can be accessed later<br />
            • You can try submitting again, or contact your administrator<br />
            • This issue has been automatically reported for investigation
          </Typography>
        </Box>

        {/* Technical Note */}
        <Typography 
          variant="body2" 
          color="textSecondary" 
          style={{ 
            fontFamily: 'Lato',
            fontStyle: 'italic',
            marginTop: 16
          }}
        >
          <strong>Note:</strong> This is a technical issue with our submission system, 
          not a problem with your survey responses. Your work is safe and complete.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          size="large"
          fullWidth
        >
          Understood
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmissionErrorDialog;